import { Inject, Injectable, InjectionToken } from '@angular/core';
import {
    Board,
    BoardCard,
    BoardCardType,
    BoardColumn,
    BoardJSON,
} from '@fry/boards/lib';

/**
 * Injection token for registering the BoardCardType objects
 *
 * The card types are registered by providing the specific subclass of BoardCardType
 * for each of the card. This is done by providing the BOARD_CARD_TYPE token with
 * `multi: true`, for example:
 *
 * ```
 * { provide: BOARD_CARD_TYPE, multi: true, useValue: OrganisationSettingsCard },
 * ```
 */
export const BOARD_CARD_TYPE = new InjectionToken<BoardCardType>('BOARD_CARD_TYPE');

/**
 * Service for managing the available Card types (BoardCardType objects) and
 * board de-serialization.
 *
 * The types are registered using the BOARD_CARD_TYPE injection token, see above.
 */
@Injectable({ providedIn: 'root' })
export class BoardCardRegistryService {

    /**
     * List of all registered card types, see BOARD_CARD_TYPE injection token
     */
    public readonly types: BoardCardType[] = [];

    public get typesSortedByTitle(): BoardCardType[] {
        return [...this.types].sort((a, b) => a.title.localeCompare(b.title));
    };

    public constructor(@Inject(BOARD_CARD_TYPE) types: BoardCardType[]) {
        this.types = types;
    }

    public findTypeByName(name: string): BoardCardType | undefined {
        return this.types.find(type => type.name === name);
    }

    /**
     * Deserialization of the Board from BoardJSON structure
     *
     * @param json - JSON data to be parsed
     */
    public boardFromJson(json: BoardJSON, boardId: string): Board {
        const columns = json.columns.map(column => {
            const cards = column.cards.map(card => {
                const type = this.findTypeByName(card.type);
                // if (!type) { throw new Error(`Unknown card type: ${card.type}`); }
                if (!type) { return null; }
                return new BoardCard(type, card.options, card.id, boardId);
            }).filter(card => card !== null) as BoardCard[];

            return new BoardColumn(column.title, cards, column.id);
        });
        return new Board(columns);
    }
}